/* eslint-disable default-case, consistent-return */
import {
  ListingKind,
  ListingPictureKind,
  MediaType,
  MediaV3,
  ProductLine,
} from '@kouto/types';
import {
  FormattedListingMedia,
  FormatterListingMediaImage,
  FormatterListingMediaVideo,
  Listing,
} from 'types/listings';
import { formatDateForReq, serializeParams } from 'utils';
import { NormalizedPublicListing } from 'types/listingV3';
import { FeaturedListingV3 } from 'types/featuredListingV3';
import { getBiggerMediaPreviewLink } from './data/medias/getBiggerMediaPreviewLink';

const PUBLIC_STORAGE_DOMAIN = 'storage.googleapis.com';

export const getListingProductLine = (
  listing: NormalizedPublicListing,
): ProductLine => {
  if (listing.productLine) return listing.productLine;

  switch (listing.kind) {
    case ListingKind.EVENT:
    case ListingKind.AUCTION:
      return ProductLine.ACTIVATE;
    case ListingKind.RESOURCE:
      return ProductLine.RESERVE;
    case ListingKind.EXPERIENCE:
      return listing.hostedBy?.id ? ProductLine.HOST : ProductLine.RESERVE;
  }
};

export const getListingRouteV1 = (
  listing: Listing,
): {
  pathname: string;
  search: string;
} => {
  let pathname = '';
  let search = '';

  switch (listing.kind) {
    case ListingKind.EVENT:
      pathname = `/event/${listing.id}`;
      if (listing.dates && listing.dates[0]) {
        search = serializeParams({
          preselectedDate: listing.dates[0],
        });
      }
      break;
    case ListingKind.RESOURCE:
      pathname = `/collection/${listing.id}`;
      break;
    case ListingKind.EXPERIENCE:
      pathname = `/e/${listing.slug}`;
  }

  return {
    pathname,
    search,
  };
};

const PARAMS_TO_PRESERVE = [
  'preselectedDate',
  'token',
  'language',
  'languageCode',
  'lang',
  'lng',
  'latest',
  'v2',
  'categoryIds',
  'vibeIds',
  'from',
  'to',
  'productLine',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref',
  'referrer',
  'gclid',
  'fbclid',
];

/**
 * The results route for wherever the filter bar sits: `/search` from the homepage,
 * `/catalogs/:id/search` from a catalog. Idempotent, so a click on "More" or the calendar while
 * already on results stays put instead of stacking `/search/search`.
 */
export const toSearchPathname = (pathname: string): string => {
  const base = pathname.replace(/\/+$/, '');
  return base.endsWith('/search') ? base : `${base}/search`;
};

/**
 * The current page's query params that every in-app navigation carries along (language, token,
 * active filters, attribution). Read from the real URL, hash-routed or not.
 */
export const getPreservedSearchParams = (): Record<string, string> => {
  const paramsToPreserve: Record<string, string> = {};

  const currentSearchParams = new URLSearchParams(
    window.location.search || window.location.hash.split('?')[1] || '',
  );

  currentSearchParams.forEach((value, key) => {
    if (PARAMS_TO_PRESERVE.includes(key)) {
      paramsToPreserve[key] = value;
    }
  });

  return paramsToPreserve;
};

/** The first event date on or after the Browse date filter, otherwise the event's first date. */
const eventDateFrom = (dates: readonly Date[], from: unknown) =>
  (typeof from === 'string' &&
    dates.find((date) => formatDateForReq(date) >= from)) ||
  dates[0];

export const getListingRoute = (
  listing: NormalizedPublicListing | FeaturedListingV3,
): {
  pathname: string;
  search: string;
} => {
  let pathname = '';
  const paramsToPreserve: Record<string, unknown> = getPreservedSearchParams();

  switch (listing.kind) {
    case ListingKind.EVENT: {
      pathname = `/event/${listing.slug ?? listing.resourceGroupCollectionId}`;
      const dates = 'dates' in listing ? listing.dates : listing.eventDates;
      if (dates && dates[0]) {
        paramsToPreserve.preselectedDate = eventDateFrom(
          dates,
          paramsToPreserve.from,
        );
      }
      break;
    }
    case ListingKind.RESOURCE:
      pathname = `/collection/${
        listing.slug ?? listing.resourceGroupCollectionId
      }`;
      break;
    case ListingKind.EXPERIENCE:
      pathname = `/e/${listing.slug}`;
      break;
  }

  return {
    pathname,
    search: serializeParams(paramsToPreserve),
  };
};

export const getProductPageTitle = ({
  product,
  translate,
}: {
  product?: ProductLine;
  translate: (label: string, options?: Record<string, string>) => string;
}): string => {
  if (!product) return translate('mainLandingPageTitle');

  switch (product) {
    case ProductLine.ACTIVATE:
      return translate('activateLandingPageTitle');
    case ProductLine.RESERVE:
      return translate('reserveLandingPageTitle');
    case ProductLine.HOST:
      return translate('hostLandingPageTitle');
  }
};

export const getProductCardButtonLabel = (
  product: ProductLine,
  translate: (label: string) => string,
): string => {
  switch (product) {
    case ProductLine.ACTIVATE:
      return translate('buyTickets');
    case ProductLine.RESERVE:
      return translate('reserve');
    case ProductLine.HOST:
      return translate('buyTickets');
  }
};

export const getProductCardLabel = (
  product: ProductLine,
  translate: (label: string) => string,
): string => {
  switch (product) {
    case ProductLine.ACTIVATE:
      return translate('activateProductCardTitle');
    case ProductLine.RESERVE:
      return translate('reserveProductCardTitle');
    case ProductLine.HOST:
      return translate('hostProductCardTitle');
  }
};

const filterResolutionSizedMediaLinks = (
  media: Listing['medias'][number],
  type: string,
) => {
  return media.links.filter(
    (l) => l.type === type && Number.isInteger(parseInt(l.resolution, 10)),
  );
};

export const getMediumMediaPreviewLink = (media: Listing['medias'][number]) => {
  const mediaLinks = filterResolutionSizedMediaLinks(media, MediaType.IMAGE);
  return (
    mediaLinks.sort(
      (ml1, ml2) => parseInt(ml2.resolution, 10) - parseInt(ml1.resolution, 10),
    )?.[Math.floor(mediaLinks.length / 2)]?.url || ''
  );
};

export const getMediumMediaLink = (media: Listing['medias'][number]) => {
  const mediaLinks = filterResolutionSizedMediaLinks(media, media.type);
  return mediaLinks.sort(
    (ml1, ml2) => parseInt(ml2.resolution, 10) - parseInt(ml1.resolution, 10),
  )[Math.floor(mediaLinks.length / 2)]?.url;
};

export const getBiggerMediaLink = (media: Listing['medias'][number]) => {
  const mediaLinks = filterResolutionSizedMediaLinks(media, media.type);
  return mediaLinks.sort(
    (ml1, ml2) => parseInt(ml2.resolution, 10) - parseInt(ml1.resolution, 10),
  )[0]?.url;
};

const isPublicStorageUrl = (url: string) => {
  try {
    const host = new URL(url).hostname;
    return host === PUBLIC_STORAGE_DOMAIN;
  } catch {
    return false;
  }
};

export const getOriginalMediaLink = (media: MediaV3): string | undefined => {
  return media.links
    .filter((link) => link.type === media.type && !isPublicStorageUrl(link.url))
    .sort(
      (a, b) =>
        getResolutionWeight(b.resolution) - getResolutionWeight(a.resolution),
    )[0]?.url;
};

const formatMediaImage = (
  media: Listing['medias'][number],
): FormatterListingMediaImage => {
  return {
    id: media.id,
    type: MediaType.IMAGE,
    kind: media.kind,
    urlDesktop: getBiggerMediaLink(media),
    urlMobile: getMediumMediaLink(media),
    altText: media.altText,
  };
};

const formatMediaVideo = (
  media: Listing['medias'][number],
): FormatterListingMediaVideo => {
  return {
    id: media.id,
    type: MediaType.VIDEO,
    kind: media.kind,
    urlDesktop: getBiggerMediaLink(media),
    urlMobile: getMediumMediaLink(media),
    previewDesktop: getBiggerMediaPreviewLink(media),
    previewMobile: getMediumMediaPreviewLink(media),
    altText: media.altText,
  };
};

export const formatListingMedia = (
  medias?: Listing['medias'],
): FormattedListingMedia[] => {
  const availableMedias = structuredClone(medias || [])
    .filter((media) => media.links.some((link) => link.type === media.type))
    .map((media) => ({
      ...media,
      links: media.links.filter(
        (link) => !Number.isNaN(parseInt(link.resolution, 10)),
      ),
    }));

  const coverMedia = availableMedias.filter((media) => media.kind === 'cover');
  const otherMedias = availableMedias.filter((media) => media.kind !== 'cover');
  const orderedMedias =
    coverMedia.length > 0 ? [...coverMedia, ...otherMedias] : availableMedias;
  return orderedMedias.flatMap((media) => {
    if (media.type === MediaType.IMAGE) {
      return formatMediaImage(media);
    }
    return formatMediaVideo(media);
  });
};

export const listingMediaIsVideo = (
  media: FormattedListingMedia,
): media is FormatterListingMediaVideo => media.type === MediaType.VIDEO;

export const getListingCoverUrl = (
  listing: Pick<Listing, 'medias'>,
  isMobile: boolean,
) => {
  const formattedMedia = formatListingMedia(listing.medias || []);

  const coverImage = formattedMedia.find(
    (media) =>
      !listingMediaIsVideo(media) && media.kind === ListingPictureKind.COVER,
  );
  if (coverImage) {
    if (isMobile) {
      return coverImage.urlMobile;
    }
    return coverImage.urlDesktop;
  }

  const coverVideo = formattedMedia.find(
    (media) =>
      listingMediaIsVideo(media) && media.kind === ListingPictureKind.COVER,
  );
  if (
    coverVideo &&
    listingMediaIsVideo(coverVideo) &&
    coverVideo.previewMobile &&
    coverVideo.previewDesktop
  ) {
    if (isMobile) {
      return coverVideo.previewMobile;
    }
    return coverVideo.previewDesktop;
  }

  const firstImage = formattedMedia.find(
    (media) => !listingMediaIsVideo(media),
  );
  if (firstImage) {
    if (isMobile) {
      return firstImage.urlMobile;
    }
    return firstImage.urlDesktop;
  }

  return '';
};

export const getListingCoverAltText = (
  listing: Pick<Listing, 'medias'>,
): string | null | undefined => {
  const formattedMedia = formatListingMedia(listing.medias || []);

  const coverMedia = formattedMedia.find(
    (media) => media.kind === ListingPictureKind.COVER,
  );

  if (coverMedia) {
    return coverMedia.altText;
  }

  // Fallback to first media if no cover
  return formattedMedia[0]?.altText;
};

/**
 * Converts a listing's medias into an ImageUriObject for ResponsiveImage.
 * Returns an object with resolution keys (e.g. "384w", "750w", "original")
 * mapped to image URLs.
 *
 * Priority: cover image > first image > cover video preview links.
 */
export const getListingCoverUriObject = (
  listing: Pick<Listing, 'medias'> | undefined | null,
): (Record<string, string> & { original: string }) | null => {
  if (!listing?.medias?.length) return null;

  const { medias } = listing;

  // Priority: cover image > any image > cover video preview
  const coverImage = medias.find(
    (media) =>
      media.kind === ListingPictureKind.COVER && media.type === MediaType.IMAGE,
  );

  const targetMedia =
    coverImage || medias.find((media) => media.type === MediaType.IMAGE);

  if (targetMedia) {
    const imageLinks = targetMedia.links.filter(
      (link) => link.type === MediaType.IMAGE,
    );
    if (imageLinks.length > 0) {
      return buildUriObjectFromLinks(imageLinks);
    }
  }

  // Fall back to cover video preview links (image-type links on a video media)
  const coverVideo = medias.find(
    (media) =>
      media.kind === ListingPictureKind.COVER && media.type === MediaType.VIDEO,
  );
  if (coverVideo) {
    const previewLinks = coverVideo.links.filter(
      (link) => link.type === MediaType.IMAGE,
    );
    if (previewLinks.length > 0) {
      return buildUriObjectFromLinks(previewLinks);
    }
  }

  return null;
};

const buildUriObjectFromLinks = (
  links: { resolution: string; url: string }[],
): (Record<string, string> & { original: string }) | null => {
  const uriObject: Record<string, string> = {};

  links.forEach((link) => {
    if (link.resolution === 'original') {
      uriObject.original = link.url;
    } else if (Number.isInteger(parseInt(link.resolution, 10))) {
      uriObject[`${link.resolution}w`] = link.url;
    }
  });

  // Ensure 'original' key exists using the highest resolution available
  if (!uriObject.original) {
    const sorted = links
      .filter((l) => Number.isInteger(parseInt(l.resolution, 10)))
      .sort((a, b) => parseInt(b.resolution, 10) - parseInt(a.resolution, 10));
    if (sorted.length > 0) {
      uriObject.original = sorted[0].url;
    }
  }

  if (!uriObject.original) return null;

  return uriObject as Record<string, string> & { original: string };
};

export const getUniquePricesForFirstAvailableDate = (
  event: Listing | undefined,
) =>
  Array.from(
    new Set(
      event?.firstAvailableDate?.sessions.flatMap(({ priceTiers }) =>
        priceTiers.map((pt) => pt.price),
      ),
    ),
  ) || [];

const getResolutionWeight = (resolution: string): number => {
  if (resolution === 'original') return Number.MAX_SAFE_INTEGER;

  const parsed = Number(resolution);
  return Number.isFinite(parsed) ? parsed : 0;
};
